/**
 * A projeção de peso do card do VITA.
 *
 * Deriva de três respostas que a pessoa JÁ deu — peso atual, meta e ritmo.
 * Nenhuma chamada nova, nenhum campo novo. Faltando qualquer uma das três,
 * devolve `null` e o card não aparece: projeção com dado faltando é
 * adivinhação apresentada como cálculo.
 *
 * Módulo puro com teste porque é aritmética que vira promessa na tela.
 */

/** Ritmo → kg por semana. Os rótulos são os que o step de timeframe grava. */
export const RITMOS = {
  'Rápido': 0.8,
  'Moderado': 0.5,
  'Sem pressa': 0.25,
  // As mesmas chaves em minúsculas. COM acento, porque é assim que
  // `'Rápido'.toLowerCase()` sai — 'rapido' sem acento nunca bateria.
  'rápido': 0.8,
  'moderado': 0.5,
  'sem pressa': 0.25,
};

/** Fallback quando o ritmo não bate com a tabela: o do meio. */
const RITMO_PADRAO = 0.5;

/** Pontos da curva. 30 segmentos bastam para a linha parecer contínua. */
const SEGMENTOS = 30;

const umaCasa = (n) => Math.abs(n).toFixed(1).replace('.', ',');

const ddmm = (d) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/**
 * Velocidade em kg/semana a partir do rótulo do ritmo.
 * @param {string} ritmo rótulo gravado pelo step
 * @returns {number} kg por semana
 */
export function velocidadeDoRitmo(ritmo) {
  if (ritmo == null) return RITMO_PADRAO;
  const chave = String(ritmo).trim();
  return RITMOS[chave] ?? RITMOS[chave.toLowerCase()] ?? RITMO_PADRAO;
}

/**
 * A curva: sobe rápido e desacelera, com uma ondulação que diminui até o fim.
 * Peso real não anda em linha reta, e uma reta prometeria uma regularidade
 * que não existe. O último ponto encosta na linha da meta, de propósito.
 * @returns {Array} pares [x, y] no sistema 300x120 do SVG
 */
export function pontosDaCurva() {
  const pontos = [];
  for (let k = 0; k <= SEGMENTOS; k += 1) {
    const t = k / SEGMENTOS;
    const suave = 1 - (1 - t) ** 1.6;
    const onda = Math.sin(t * Math.PI * 4) * 0.045 * (1 - t);
    pontos.push([t * 300, 12 + (1 - Math.min(1, suave + onda)) * 84]);
  }
  pontos[SEGMENTOS][1] = 12; // termina exatamente na meta
  return pontos;
}

/**
 * A projeção inteira, pronta para o card.
 * @param {object} answers respostas acumuladas do onboarding
 * @param {Date} agora data base (injetável para o teste)
 * @returns {object|null} dados do card, ou null quando falta dado
 */
export function projecaoDePeso(answers = {}, agora = new Date()) {
  const atual = Number(answers.CURRENT_WEIGHT);
  const meta = Number(answers.TARGET_WEIGHT);
  const ritmo = answers.TIMEFRAME;

  if (!Number.isFinite(atual) || !Number.isFinite(meta) || !ritmo) return null;

  const diferenca = meta - atual;
  // Meta igual ao peso atual não é projeção de nada.
  if (Math.abs(diferenca) < 0.05) return null;

  const velocidade = velocidadeDoRitmo(ritmo);
  const semanas = Math.max(1, Math.round(Math.abs(diferenca) / velocidade));
  const ganho = diferenca > 0;

  const pontos = pontosDaCurva();
  const caminho = pontos
    .map((p, k) => `${k ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(' ');

  const fim = new Date(agora.getTime() + semanas * 7 * 86400000);
  const verbo = ganho ? 'ganha' : 'perde';

  return {
    ganho,
    semanas,
    diferenca,
    titulo: ganho ? 'PROJEÇÃO DE GANHO' : 'PROJEÇÃO DE PERDA',
    resumo: `${umaCasa(diferenca)} kg em ${semanas} ${semanas === 1 ? 'semana' : 'semanas'}`,
    caminho,
    area: `${caminho} L300 100 L0 100 Z`,
    dataInicial: ddmm(agora),
    dataFinal: ddmm(fim),
    nota:
      `Com base no seu perfil, você ${verbo} ${umaCasa(diferenca)} kg em cerca de `
      + `${semanas} ${semanas === 1 ? 'semana' : 'semanas'} no ritmo `
      + `${String(ritmo).toLowerCase()}. É uma projeção — dieta, treino e sono `
      + 'mudam o resultado.',
    // Único por instância: com id fixo, dois cards na mesma página colidem.
    idDoGradiente: `ob-area-${Math.random().toString(36).slice(2, 8)}`,
  };
}

export default projecaoDePeso;
